import React, { useState } from "react";
import { IPayoutItem, PayoutStatus, SortByOptions } from "./AdminPayoutsPage";
import {
  Box,
  Typography,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  useMediaQuery,
  makeStyles,
  Paper,
  useTheme,
} from "@material-ui/core";
import SortedTableCell from "../../../../elements/table/SortedTableCell";
import { UniversalDropdown } from "../../../../elements/UniversalDropdow";
import SearchField from "../SearchField";
import { Link } from "react-router-dom";
import { adminGetPayoutDetailsAPI } from "../../../../../api/payoutsApi";
import { showToast } from "../../../../../utils/toast";
import { Ban, Check, Clock8, DollarSign, Eye, RefreshCw, X } from "lucide-react";
import UploadButton from "../../../../elements/UploadButton";
import { DetailReceipt } from "./DetailReceipt";
import { DetailPopup } from "../DetailPopup";
import { DetailDialog } from "../DetailPopupMobile";
import clsx from "clsx";

const useStyles = makeStyles({
  tableWrap: {
    background: "#141414",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.08)",
  },
  tableHeaderCell: {
    color: "#fff", fontWeight: 500, fontSize: 16,
    background: "#74747426",
    borderRight: "1px solid #74747426",
    borderBottom: 0,
    lineHeight: "normal",
    height: "auto",
    verticalAlign: "center",

    "&.active": {
      color: "#FF78AA",
    },
    "& .MuiOutlinedInput-inputAdornedStart": {
      background: "transparent",
      border: 0
    }
  },
  tableBodyCell: {
    color: "#fff",
    border: "1px solid #74747426",
    fontSize: 14,
    lineHeight: "normal",
    height: "auto",
    padding: 8
  },
  userNameLink: {
    color: "white",
    textDecoration: "underline",
    textDecorationColor: "white",
    "&:hover": {
      color: "#FE5A97",
      textDecorationColor: "#FE5A97"
    }
  },
  outlinedIconButton: {
    color: "#fff",
    border: "1px solid #74747480",
    borderRadius: 6,
    padding: 8,
    fontSize: 14,
    margin: "0 0 0 auto",
  },


  //Mobile
  card: {
    padding: 16,
    backgroundColor: "#131313",
    color: "#fff",
    border: "1px solid #1F1F1F",
    marginBottom: 8,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
  },
  field: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontWeight: 600,
    fontSize: 12,
    color: "#FFFFFF",
  },
  value: {
    fontSize: 12,
    color: "#FFFFFFCC",
  },
  uploadButton: {
    padding: "8px 23px"
  },
  buttonBox: {
    border: "1px solid #74747426",
    borderRadius: 4,
    width: "max-content",
    padding: "10px 8px",
    margin: "16px 0",
    "& .MuiButton-text": {
      fontSize: 12
    }
  }
});

type PayoutsProps = {
  items: IPayoutItem[],
  loading: boolean;
  sortBy: string;
  changeSort: (field: string) => void;
  load: (p?: number, l?: number, tmpCreatorUsername?: string | undefined, tmpStatus?: "" | PayoutStatus | undefined) => Promise<void>
  limit: number;
  status: "" | PayoutStatus;
  setStatus: React.Dispatch<React.SetStateAction<"" | PayoutStatus>>;
  setPage: (page: number) => void;
  creatorUsername: string;
  setCreatorUsername: React.Dispatch<React.SetStateAction<string>>
  applyFilters: () => void;
  setViewDestOpen: (val: boolean) => void;
  setReceiptFile: (file: File | null) => void;
  setReceiptOpen: (val: boolean) => void;
  // setMarkPaidId: (id: string) => void;
  // setProviderRef: (val: string) => void;
  // setNote: (val: string) => void;
  // setMarkPaidOpen: (val: boolean) => void;
  setReceiptId: (id: string) => void;
  viewDestOpen: boolean;
};

export function PayoutsTable({ items, loading, sortBy, changeSort, load,
  limit, status, setStatus, setPage, creatorUsername, setCreatorUsername, applyFilters,
  setViewDestOpen, setReceiptFile, setReceiptOpen,
  setReceiptId, viewDestOpen }: PayoutsProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles();
  const [showCreatorSearch, setShowCreatorSearch] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [info, setInfo] = useState<IPayoutItem | null>(null);
  const [destRow, setDestRow] = useState<IPayoutItem | null>(null);
  const [destLoading, setDestLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const destinationFields = [
    { label: "Method", value: destRow?.method },
    ...(destRow?.method === "bank"
      ? [
        { label: "Beneficiary", value: destRow?.destination?.beneficiaryName },
        { label: "IBAN", value: destRow?.destination?.iban },
        { label: "Account #", value: destRow?.destination?.accountNumber },
        { label: "SWIFT/BIC", value: destRow?.destination?.swiftBic },
        { label: "Bank name", value: destRow?.destination?.bankName },
        { label: "Bank country", value: destRow?.destination?.bankCountry },
      ]
      : []),
    ...(destRow?.method === "paypal" || destRow?.method === "wise"
      ? [{ label: "Email", value: destRow?.destination?.email }]
      : []),
  ]

  const currency = (n?: number) => `$${Number(n || 0).toFixed(2)}`;
  const fmtDate = (d?: string) => (d ? new Date(d).toLocaleString() : "—");

  const statusOptions: { label: string; value: PayoutStatus | "" }[] = [
    { label: "All", value: "" },
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Processing", value: "processing" },
    { label: "Paid", value: "paid" },
    { label: "Rejected", value: "rejected" },
    { label: "Canceled", value: "canceled" },
  ];

  const sortByOptions: { label: string; value: SortByOptions | "" }[] = [
    { label: "All", value: "" },
    { label: "Amount", value: "amount" },
    { label: "Created At", value: "createdAt" },
    { label: "Paid At", value: "paidAt" },
  ];

  const StatusChip = ({ status }: { status: string }) => {
    let color = "#FFFFFF";
    let style = isMobile ? { margin: 0, height: 12, width: 12 } : { marginRight: 4 }
    let icon = <RefreshCw style={style} color={color} />

    if (status === "paid") {
      color = "#85F96B";
      icon = <DollarSign style={style} color={color} />;
    }
    if (status === "processing") {
      color = "#CEE4FF";
      icon = <RefreshCw style={style} color={color} />;
    }
    if (status === "pending") {
      icon = <Clock8 style={style} color={color} />;
    }
    if (status === "approved") {
      color = "#D2FFC8";
      icon = <Check style={style} color={color} />;
    }
    if (status === "canceled") {
      color = "#FF92B1";
      icon = <Ban style={style} color={color} />;
    }
    if (status === "rejected") {
      color = "#FF4977";
      icon = <X style={style} color={color} />;
    }

    return (
      <Chip
        size="small"
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        icon={icon}
        style={{ color, backgroundColor: "transparent", fontSize: isMobile ? 12 : 14 }}
      />
    );
  };

  const clearSearchInputInCollumn = () => {
    const cleared = "";
    setCreatorUsername(cleared);
    setShowCreatorSearch(false);
    setPage(0);
    load(0, limit, cleared);
  }

  const handleCloseDropdown = (value?: PayoutStatus | "") => {
    if (value !== undefined) {
      setStatus(value);
      setPage(0);
      load(0, limit, creatorUsername, value);
    }
  };
  const handleCloseSortByDropdown = (value?: string | "") => {
    if (value !== undefined) {
      changeSort(value);
      setPage(0);
      load(0, limit, creatorUsername);
    }
  };

  // const reject = async (id: string) => {
  //   try {
  //     const text = window.prompt("Reason (note):") || "";
  //     await adminRejectPayoutAPI(id, { note: text });
  //     showToast({ message: "Rejected", success: true });
  //     load();
  //   } catch (e) {
  //     console.error(e);
  //     showToast({ message: (e as any)?.message || "Reject failed", err: true });
  //   }
  // };


  // const approve = async (id: string) => {
  //   try {
  //     await adminApprovePayoutAPI(id);
  //     showToast({ message: "Approved", success: true });
  //     load();
  //   } catch (e) {
  //     console.error(e);
  //     showToast({
  //       message: (e as any)?.message || "Approve failed",
  //       err: true,
  //     });
  //   }
  // };

  // const openMarkPaid = (id: string) => {
  //   setMarkPaidId(id);
  //   setProviderRef("");
  //   setNote("");
  //   setMarkPaidOpen(true);
  // };

  // view destination — тягнемо повністю з бекенду
  const openViewDest = async (row: IPayoutItem) => {
    try {
      setDestRow(row);
      setDestLoading(true);
      setViewDestOpen(true);
      const full = await adminGetPayoutDetailsAPI(row._id);
      setDestRow(prev => ({ ...(prev || row), ...full }));
    } catch (e) {
      showToast({ message: "Failed to load destination details", err: true });
    } finally {
      setDestLoading(false);
    }
  };

  // upload receipt
  const openReceipt = (rowId: string) => {
    setReceiptId(rowId);
    setReceiptFile(null);
    setReceiptOpen(true);
  };

  const viewReceipt = (id: string) => {
    setShowReceipt(true);
    const user = items.find(i => i._id === id) ?? null;
    setInfo(user)
  }

  if (isMobile) {
    return (
      <>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box className={classes.buttonBox}>
            <UniversalDropdown
              label="Status"
              value={status}
              options={statusOptions}
              onChange={handleCloseDropdown} />
          </Box>
          <Box className={classes.buttonBox}>
            <UniversalDropdown
              label="Sort by"
              value={sortBy}
              options={sortByOptions}
              onChange={handleCloseSortByDropdown} />
          </Box>
        </Box>
        <div>
          {items.map(row => (
            <Paper key={row._id} className={classes.card}>
              <div className={classes.grid}>
                <div className={classes.field}>
                  <span className={classes.label}>User name</span>
                  <span className={classes.value}>@{row.creator.username}</span>
                  <Link to={"/" + row.creator.username} className={clsx(classes.userNameLink, classes.value)}>@{row.creator.username}</Link>
                </div>
                <div className={classes.field}>
                  <span className={classes.label}>Status</span>
                  <span className={classes.value}><StatusChip status={row.status} /></span>
                </div>
                <div className={classes.field}>
                  <span className={classes.label}>Amount, USD</span>
                  <span className={classes.value}>{currency(row.amount)}</span>
                </div>
                <div className={classes.field}>
                  <span className={classes.label}>Destination</span>
                  <span className={classes.value}>{row.destinationMasked}</span>
                </div>
                <div className={classes.field}>
                  <span className={classes.label}>Paid at</span>
                  <span className={classes.value}>{fmtDate(row.paidAt)}</span>
                </div>
                <Box display="flex" style={{ gap: 8 }}>
                  {row.method && (
                    <IconButton
                      size="small"
                      onClick={() => openViewDest(row)}
                      className={classes.outlinedIconButton}
                    >
                      <Eye size={16} />
                    </IconButton>
                  )}
                  <UploadButton text="View receipt" isIcon={false} onClick={() => viewReceipt(row._id)} styles={classes.uploadButton} />
                </Box>
              </div>
            </Paper>

          ))}
          {!loading && items.length === 0 && (
            <Typography style={{ textAlign: "center", color: "rgba(255,255,255,0.6)" }}>
              No payouts found
            </Typography>
          )}
        </div>
        <DetailReceipt open={showReceipt} onClose={() => setShowReceipt(false)} data={info} />

        {/* View Destination */}
        <DetailDialog
          open={viewDestOpen}
          onClose={() => setViewDestOpen(false)}
          title="Destination details"
          fields={destinationFields ?? null}
        />
      </>
    );
  }

  // Dekstop table
  return (
    <Box className={classes.tableWrap}>
      <Table size="small" style={{ borderCollapse: "collapse" }}>
        <TableHead>
          <TableRow>
            <SortedTableCell
              name="Created"
              field="createdAt"
              sortBy={sortBy}
              changeSort={changeSort}
            />

            {/* creatorUsername search  */}
            <TableCell className={classes.tableHeaderCell} style={showCreatorSearch ? { padding: 0 } : {}}>
              <div style={showCreatorSearch ? {
                border: "1px solid #747474",
              } : {}}>
                <SearchField showSearch={showCreatorSearch} setShowSearch={setShowCreatorSearch} input={creatorUsername} setInput={setCreatorUsername} clearSearch={clearSearchInputInCollumn} applyFilters={applyFilters} />
              </div>
            </TableCell>

            <SortedTableCell
              name="Amount, USD"
              field="amount"
              sortBy={sortBy}
              changeSort={changeSort}
            />
            <TableCell className={classes.tableHeaderCell}>
              <UniversalDropdown
                label="Status"
                value={status}
                options={statusOptions}
                onChange={handleCloseDropdown} />

            </TableCell>


            <TableCell className={classes.tableHeaderCell}>Method</TableCell>
            <TableCell className={classes.tableHeaderCell}>
              Destination
            </TableCell>

            <SortedTableCell name="Paid at" field="paidAt" sortBy={sortBy} changeSort={changeSort} />
            {/*<TableCell className={classes.tableHeaderCell}>ProviderRef</TableCell>*/}
            {/*<TableCell className={classes.tableHeaderCell}>Note</TableCell>*/}
            <TableCell className={classes.tableHeaderCell} align="right">
              {/* Actions */}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(row => (
            <TableRow key={row._id}>
              <TableCell className={classes.tableBodyCell}>
                {fmtDate(row.createdAt)}
              </TableCell>
              <TableCell className={classes.tableBodyCell}>
                <Link to={"/" + row.creator.username} className={classes.userNameLink}>@{row.creator.username}</Link>
              </TableCell>
              <TableCell className={classes.tableBodyCell} style={{ textAlign: "right" }}>
                {currency(row.amount)}
                {/* {row.currency || "USD"} */}
              </TableCell>
              <TableCell className={classes.tableBodyCell}>
                <StatusChip status={row.status} />
              </TableCell>
              <TableCell className={classes.tableBodyCell}>
                {row.method || "—"}
              </TableCell>
              <TableCell className={classes.tableBodyCell}>
                <Box display="flex" alignItems="center" gridGap={8 as any}>
                  <span>{row.destinationMasked || "—"}</span>
                  {row.method && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget)
                        openViewDest(row)
                      }}
                      className={classes.outlinedIconButton}
                    >
                      <Eye size={16} />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
              <TableCell className={classes.tableBodyCell}>
                {fmtDate(row.paidAt)}
              </TableCell>
              {/*<TableCell className={classes.tableBodyCell}>{row.providerRef || "—"}</TableCell>*/}
              {/*<TableCell className={classes.tableBodyCell}>{row.note || "—"}</TableCell>*/}
              <TableCell
                align="right"
                className={classes.tableBodyCell}
                style={{
                  whiteSpace: "nowrap",
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                  minHeight: "48px",
                }}
              >
                {row.receiptMediaUrl && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      window.open(row.receiptMediaUrl as string, "_blank")
                    }
                    style={{
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.35)",
                      fontSize: 14,
                      maxHeight: 32
                    }}
                  >
                    Receipt
                  </Button>
                )}

                <UploadButton text="Upload receipt" iconSize={16} onClick={() => openReceipt(row._id)} />

                {/* {row.status === "pending" && (
                  <Button
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={() => approve(row._id)}
                    style={{ fontSize: 14, maxHeight: 32 }}
                  >
                    Approve
                  </Button>
                )} */}
                {/* {(row.status === "approved" ||
                  row.status === "processing") && (
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      onClick={() => openMarkPaid(row._id)}
                      style={{ fontSize: 14, maxHeight: 32 }}
                    >
                      Mark paid
                    </Button>
                  )} */}
                {/* {["pending", "approved", "processing"].includes(
                  row.status
                ) && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => reject(row._id)}
                      style={{
                        color: "#fff",
                        borderColor: "rgba(255,255,255,0.4)",
                        fontSize: 14,
                        maxHeight: 32
                      }}
                    >
                      Reject
                    </Button>
                  )} */}
              </TableCell>
            </TableRow>
          ))}
          {!loading && items.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={10}
                style={{
                  textAlign: "center",
                  padding: 24,
                  color: "rgba(255,255,255,0.6)",
                }}
              >
                No payouts found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {/* View Destination */}
      <DetailPopup title="Destination details" fields={destinationFields ?? null} viewOpen={viewDestOpen} setViewOpen={setViewDestOpen} anchorEl={anchorEl} setAnchorEl={setAnchorEl} />

    </Box>
  );
}
